import * as React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useRouteContext } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { Separator } from '@ooh/ui/components/separator';
import { POI_ICON_DEFS } from '@ooh/ui/components/map-view-poi-icons';
import { mediaUnitPoiQueries } from '../api/queries';

interface NearbyPoiRowProps {
  name: string;
  osmKey: string;
  osmValue: string;
  distanceM: number;
  distanceLabel: string;
}

function PoiCategoryIcon({ osmKey, osmValue }: { osmKey: string; osmValue: string }) {
  const iconKey = `${osmKey}-${osmValue}`;
  const def = POI_ICON_DEFS[iconKey] ?? POI_ICON_DEFS['poi-fallback']!;
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 28 28"
      className="shrink-0"
      aria-label={iconKey}
      data-testid={`nearby-poi-icon-${iconKey}`}
    >
      <rect width="28" height="28" rx="4" fill={def.color} />
      <path d={def.svgPath} fill="white" />
    </svg>
  );
}

function NearbyPoiRow({ name, osmKey, osmValue, distanceLabel }: NearbyPoiRowProps) {
  return (
    <div className="flex items-center gap-2 text-[12px]">
      <PoiCategoryIcon osmKey={osmKey} osmValue={osmValue} />
      <span className="flex-1 truncate text-foreground">{name}</span>
      <span className="text-muted-foreground shrink-0">{distanceLabel}</span>
    </div>
  );
}

function SkeletonRow() {
  return (
    <div className="flex items-center gap-2">
      <div className="w-4 h-4 rounded bg-muted animate-pulse shrink-0" />
      <div className="flex-1 h-3 rounded bg-muted animate-pulse" />
      <div className="w-8 h-3 rounded bg-muted animate-pulse shrink-0" />
    </div>
  );
}

interface NearbyPoisSectionProps {
  mediaUnitId: string | number;
}

export function NearbyPoisSection({ mediaUnitId }: NearbyPoisSectionProps) {
  const { auth } = useRouteContext({ from: '__root__' });
  const { t } = useTranslation('map');

  const { data, isLoading } = useQuery({
    ...mediaUnitPoiQueries.nearbyPois(auth, String(mediaUnitId), 5),
    staleTime: 60 * 1000, // 1 min
  });

  const pois = Array.isArray(data) ? data : [];

  return (
    <>
      <Separator className="my-2" />
      <div data-testid="nearby-pois-section">
        <p className="text-[11px] font-medium text-muted-foreground mb-1.5">
          {t('pois.nearbySection.heading')}
        </p>
        {isLoading ? (
          <div className="space-y-1.5">
            <SkeletonRow />
            <SkeletonRow />
            <SkeletonRow />
          </div>
        ) : pois.length === 0 ? (
          <p className="text-[11px] text-muted-foreground italic">
            {t('pois.nearbySection.empty')}
          </p>
        ) : (
          <div className="space-y-1.5">
            {pois.map((poi) => (
              <NearbyPoiRow
                key={poi.poi_id}
                name={poi.name}
                osmKey={poi.osm_key}
                osmValue={poi.osm_value}
                distanceM={Math.round(poi.distance_m)}
                distanceLabel={t('pois.nearbySection.distanceM', {
                  distance: Math.round(poi.distance_m),
                })}
              />
            ))}
          </div>
        )}
      </div>
    </>
  );
}
